import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  ThemeProvider,
  createTheme,
  CssBaseline,
  Paper,
} from "@mui/material";

// Import wizard components
import WizardApp from "./components/Wizard/WizardApp";

import Sidebar from "./components/General Functions/Sidebar";
import AppIntroduction from "./components/General Functions/AppIntroduction";
import PreviewPanel from "./components/General Functions/PreviewPanel";
import PHCGGenerator from "./components/General Functions/PHCGGenerator";
import CharterSectionPreview from "./components/CharterSection/CharterSectionPreview";
import CharterSectionGenerator from "./components/CharterSection/CharterSectionGenerator";
import CharterTypesPreview from "./components/CharterTypes/CharterTypesPreview";
import CharterTypesGenerator from "./components/CharterTypes/CharterTypesGenerator";
import ThreeStepsPreview from "./components/ThreeSteps/ThreeStepsPreview";
import ThreeStepsGenerator from "./components/ThreeSteps/ThreeStepsGenerator";
import DYPToolPreview from "./components/DYPTool/DYPToolPreview";
import DYPToolGenerator from "./components/DYPTool/DYPToolGenerator";
import PartnersBenefitsPreview from "./components/PartnerBenefits/PartnersBenefitsPreview";
import PartnersBenefitsGenerator from "./components/PartnerBenefits/PartnersBenefitsGenerator";
import PCProcessPreview from "./components/PCProcess/PCProcessPreview";
import PCProcessGenerator from "./components/PCProcess/PCProcessGenerator";
import DYPAdvantagesPreview from "./components/DYPAdvantages/DYPAdvantagesPreview";
import DYPAdvantagesGenerator from "./components/DYPAdvantages/DYPAdvantagesGenerator";
import AssistanceLevelsPreview from "./components/AssistanceLevels/AssistanceLevelsPreview";
import AssistanceLevelsGenerator from "./components/AssistanceLevels/AssistanceLevelsGenerator";
import BookPromoPreview from "./components/BookPromo/BookPromoPreview";
import BookPromoGenerator from "./components/BookPromo/BookPromoGenerator";
import TestimonialsPreview from "./components/Testimonials/TestimonialsPreview";
import TestimonialsGenerator from "./components/Testimonials/TestimonialsGenerator";
import SliversOfAmbiguityPreview from "./components/SliversOfAmbiguity/SliversPreview";
import SliversOfAmbiguityGenerator from "./components/SliversOfAmbiguity/SliversGenerator";
import DYPLicensedUserPreview from "./components/DYPLicensedUser/DYPLicensedUserPreview";
import DYPLicensedUserGenerator from "./components/DYPLicensedUser/DYPLicensedUserGenerator";
import FullPagePreview from "./components/FullPagePreview/FullPagePreview";

// Import Hero section components
import HeroSection from "./components/Hero/Hero";
import HeroSectionGenerator from "./components/Hero/HeroSectionGenerator";
import { defaultHeroConfig } from "./components/Hero/HeroSectionShared";

// Define shared styling constants for the entire application
const STYLE_CONSTANTS = {
  // Unified Color Palette
  // Primary Colors (Blues) - consolidated from multiple similar shades
  primaryBlue: "#1C436A", // Main brand blue (was #1C436A, #1e3553, #1e4164, #2F475E)
  primaryBlueDark: "#16365A", // Darker variant for hovers
  primaryBlueLight: "#1976d2", // Lighter blue for interactive elements

  // Secondary Colors (Greys) - consolidated neutral palette
  white: "#ffffff",
  lightGray: "#f5f7fa", // Light backgrounds (was #f5f7fa, #ecf0f1, #e6ecef)
  cardGray: "#E5E7EA", // Card/container backgrounds
  borderGray: "#ddd", // Borders and dividers
  darkText: "#333333", // Dark text (was #333333, #2c3e50, #4A4A4A)

  // Accent Colors (Gold/Yellow) - unified accent palette
  accentGold: "#CE950F", // Main accent color (was #CE950F)
  accentGoldLight: "#d5ad36", // Light accent variant

  // Legacy aliases for backward compatibility
  primaryYellow: "#CE950F", // Alias for accentGold

  // Fonts
  headingFont: "serif",
  bodyFont: "sans-serif",

  // Common styling values
  borderRadius: 4,
  imageRadius: 8,
  transitionSpeed: 0.3,

  // Common spacing
  defaultMargin: 20,
};

// Create theme using the styling constants
const theme = createTheme({
  palette: {
    primary: { main: STYLE_CONSTANTS.primaryBlue },
    secondary: { main: STYLE_CONSTANTS.primaryYellow },
  },
  typography: {
    fontFamily: STYLE_CONSTANTS.bodyFont,
    h1: { fontFamily: STYLE_CONSTANTS.headingFont },
    h2: { fontFamily: STYLE_CONSTANTS.headingFont },
    h3: { fontFamily: STYLE_CONSTANTS.headingFont },
    h4: { fontFamily: STYLE_CONSTANTS.headingFont },
    h5: { fontFamily: STYLE_CONSTANTS.headingFont },
    h6: { fontFamily: STYLE_CONSTANTS.headingFont },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: STYLE_CONSTANTS.borderRadius,
        },
        containedPrimary: {
          backgroundColor: STYLE_CONSTANTS.primaryBlue,
          "&:hover": {
            backgroundColor: STYLE_CONSTANTS.primaryBlueDark,
          },
        },
        containedSecondary: {
          backgroundColor: STYLE_CONSTANTS.accentGold,
          "&:hover": {
            backgroundColor: "#B5850D", // Slightly darker gold on hover
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: STYLE_CONSTANTS.borderRadius,
        },
      },
    },
  },
});

const sectionTitles = {
  introduction: "Introduction & Guide",
  hero: "Hero Section", // Added hero section title
  PHCG: "About Post-Haste Consulting Group (PHCG)",
  charter: "What Is a PC?",
  charterTypes: "Who Should Develop a Charter?",
  threeSteps: "Three Steps to Partnership",
  dypTool: "The DYP Tool",
  partners: "Partners Benefits Block",
  pcProcess: "All the Critical Topics Are Covered",
  dypAdvantages: "DYP Advantages",
  assistanceLevels: "Partners Get Assistance",
  bookPromo: "Partnership Charter Book",
  testimonials: "Client Testimonials",
  sliversOfAmbiguity: "Slivers of Ambiguity",
  dypLicensedUser: "DYP Licensed User",
  fullPagePreview: "Full Page Preview",
};

// Original App component renamed to LegacyApp (Advanced Mode)
function LegacyApp() {
  const [activeSection, setActiveSection] = useState(() => {
    const savedSection = localStorage.getItem("activeSection");
    return savedSection || "introduction";
  });

  useEffect(() => {
    localStorage.setItem("activeSection", activeSection);
  }, [activeSection]);

  // Introduction - No config needed

  // Hero Section
  const [heroConfig, setHeroConfig] = useState({
    backgroundImageUrl: "/hero_bg.png",
    overlayColor: `${STYLE_CONSTANTS.primaryBlue}B3`, // 70% opacity using hex
    textColor: STYLE_CONSTANTS.white,
    headingFont: STYLE_CONSTANTS.headingFont,
    taglineText: "...never operate a partnership without one",
    logoUrl: "/phcg_logo.png",
    height: "50vh",
  });

  // About PHCG
  const [PHCGConfig, setPHCGConfig] = useState({
    backgroundColor: STYLE_CONSTANTS.primaryBlue,
    textColor: STYLE_CONSTANTS.white,
    headingFont: STYLE_CONSTANTS.headingFont,
    bodyFont: STYLE_CONSTANTS.bodyFont,
    buttonColor: STYLE_CONSTANTS.accentGold,
    buttonText: null, // Will use dynamic text based on company name/acronym
    buttonRadius: STYLE_CONSTANTS.borderRadius,
  });

  // What Is a PC?
  const [charterConfig, setCharterConfig] = useState({
    backgroundColor: STYLE_CONSTANTS.primaryBlue,
    textColor: STYLE_CONSTANTS.white,
    headingColor: STYLE_CONSTANTS.white,
    accentColor: STYLE_CONSTANTS.accentGold,
    headingFont: STYLE_CONSTANTS.headingFont,
    bodyFont: STYLE_CONSTANTS.bodyFont,
    logoSrc: "/tpci_logo.svg",
    imageSrc: "/chartersection_computer.jpg",
  });

  // Who Should Develop a Charter?
  const [charterTypesConfig, setCharterTypesConfig] = useState({
    backgroundColor: STYLE_CONSTANTS.white,
    textColor: STYLE_CONSTANTS.darkText,
    headingColor: STYLE_CONSTANTS.primaryBlue,
    headingFont: STYLE_CONSTANTS.headingFont,
    bodyFont: STYLE_CONSTANTS.bodyFont,
  });

  // Three Steps to Partnership
  const [threeStepsConfig, setThreeStepsConfig] = useState({
    backgroundColor: STYLE_CONSTANTS.white,
    textColor: STYLE_CONSTANTS.darkText,
    headingColor: STYLE_CONSTANTS.primaryBlue,
    cardBackgroundColor: STYLE_CONSTANTS.cardGray,
    stepBadgeColor: STYLE_CONSTANTS.primaryBlue,
    stepBadgeTextColor: STYLE_CONSTANTS.white,
    headingFont: STYLE_CONSTANTS.headingFont,
    bodyFont: STYLE_CONSTANTS.bodyFont,
    discoverIconSrc: "https://tpci.vercel.app/step1.png",
    discussIconSrc: "https://tpci.vercel.app/step2.png",
    documentIconSrc: "https://tpci.vercel.app/step3.png",
    cardShadow: false,
  });

  // The DYP Tool
  const [dypToolConfig, setDypToolConfig] = useState({
    backgroundColor: STYLE_CONSTANTS.lightGray,
    textColor: STYLE_CONSTANTS.darkText,
    headingColor: STYLE_CONSTANTS.primaryBlue,
    headingFont: STYLE_CONSTANTS.headingFont,
    bodyFont: STYLE_CONSTANTS.bodyFont,
    imageSrc: "/dyp_tool.png",
    imageRadius: STYLE_CONSTANTS.imageRadius,
  });

  // Partners Benefits Block
  const [partnersBenefitsConfig, setPartnersBenefitsConfig] = useState({
    backgroundColor: STYLE_CONSTANTS.lightGray,
    textColor: STYLE_CONSTANTS.darkText,
    headingColor: STYLE_CONSTANTS.primaryBlue,
    accentColor: STYLE_CONSTANTS.accentGold,
    headingFont: STYLE_CONSTANTS.headingFont,
    bodyFont: STYLE_CONSTANTS.bodyFont,
    buttonColor: STYLE_CONSTANTS.accentGold,
    buttonRadius: STYLE_CONSTANTS.borderRadius,
    imageRadius: STYLE_CONSTANTS.imageRadius,
    imageSrc: `https://placehold.co/600x400/e6e6e6/${STYLE_CONSTANTS.primaryBlue.replace(
      "#",
      ""
    )}?text=Partnership+diagram`,
  });

  // All the Critical Topics Are Covered (PC Process)
  const [pcProcessConfig, setPcProcessConfig] = useState({
    backgroundColor: STYLE_CONSTANTS.primaryBlue,
    textColor: STYLE_CONSTANTS.white,
    accentColor: STYLE_CONSTANTS.accentGold,
    headingFont: STYLE_CONSTANTS.headingFont,
    bodyFont: STYLE_CONSTANTS.bodyFont,
    buttonColor: STYLE_CONSTANTS.accentGold,
    buttonText: null, // Will use dynamic text based on company name/acronym
    buttonRadius: STYLE_CONSTANTS.borderRadius,
    accordionRadius: STYLE_CONSTANTS.borderRadius,
    transitionSpeed: STYLE_CONSTANTS.transitionSpeed,
  });

  // DYP Advantages
  const [dypAdvantagesConfig, setDypAdvantagesConfig] = useState({
    backgroundColor: STYLE_CONSTANTS.primaryBlue,
    textColor: STYLE_CONSTANTS.white,
    accentColor: STYLE_CONSTANTS.accentGold,
    headingFont: STYLE_CONSTANTS.headingFont,
    bodyFont: STYLE_CONSTANTS.bodyFont,
    accordionRadius: STYLE_CONSTANTS.borderRadius,
    transitionSpeed: STYLE_CONSTANTS.transitionSpeed,
  });

  // Partners Get Assistance
  const [assistanceLevelsConfig, setAssistanceLevelsConfig] = useState({
    backgroundColor: STYLE_CONSTANTS.white,
    textColor: STYLE_CONSTANTS.darkText,
    headingColor: STYLE_CONSTANTS.primaryBlue,
    accentColor: STYLE_CONSTANTS.accentGold,
    headingFont: STYLE_CONSTANTS.headingFont,
    bodyFont: STYLE_CONSTANTS.bodyFont,
    imageRadius: STYLE_CONSTANTS.imageRadius,
    imageSrc: "/assistance_levels_diagram.png",
  });

  // Partnership Charter Book
  const [bookPromoConfig, setBookPromoConfig] = useState({
    backgroundColor: STYLE_CONSTANTS.accentGold,
    textColor: STYLE_CONSTANTS.white,
    headingFont: STYLE_CONSTANTS.headingFont,
    bodyFont: STYLE_CONSTANTS.bodyFont,
    borderRadius: 5, // Keeping this specific value as it's intentionally larger
    marginTop: STYLE_CONSTANTS.defaultMargin, // Added margin top
    marginBottom: STYLE_CONSTANTS.defaultMargin, // Added margin bottom
    bookImageSrc: "https://tpci.vercel.app/book_partnership_charter.png",
    contactLink: "https://example.com/contact",
    barnesNobleLogo: "https://tpci.vercel.app/logo_barnes_noble.png",
    googlePlayLogo: "https://tpci.vercel.app/logo_google_play.png",
    amazonLogo: "https://tpci.vercel.app/logo_amazon.png",
    indieBoundLogo: "https://tpci.vercel.app/logo_indie_bound.png",
    audibleLogo: "https://tpci.vercel.app/logo_audible.png",
    invertLogos: false,
  });

  // Client Testimonials
  const [testimonialsConfig, setTestimonialsConfig] = useState({
    backgroundColor: STYLE_CONSTANTS.primaryBlue,
    textColor: STYLE_CONSTANTS.white,
    headingFont: STYLE_CONSTANTS.headingFont,
    bodyFont: STYLE_CONSTANTS.bodyFont,
    testimonialImage: "https://tpci.vercel.app/testimonial_photo.jpeg",
  });

  // Slivers of Ambiguity
  const [sliversOfAmbiguityConfig, setSliversOfAmbiguityConfig] = useState({
    backgroundColor: STYLE_CONSTANTS.lightGray,
    textColor: STYLE_CONSTANTS.darkText,
    headingColor: STYLE_CONSTANTS.primaryBlue,
    accentColor: STYLE_CONSTANTS.accentGold,
    headingFont: STYLE_CONSTANTS.headingFont,
    bodyFont: STYLE_CONSTANTS.bodyFont,
  });

  // DYP Licensed User
  const [dypLicensedUserConfig, setDypLicensedUserConfig] = useState({
    backgroundColor: STYLE_CONSTANTS.primaryBlue,
    textColor: STYLE_CONSTANTS.white,
    accentColor: STYLE_CONSTANTS.accentGold,
    headingFont: STYLE_CONSTANTS.headingFont,
    bodyFont: STYLE_CONSTANTS.bodyFont,
    buttonText: null,  // Will use dynamic text based on company acronym
    buttonRadius: STYLE_CONSTANTS.borderRadius,
    organizationName: null,  // Will use company acronym or placeholder
  });

  const renderSection = () => {
    switch (activeSection) {
      // Introduction & Guide
      case "introduction":
        return <AppIntroduction />;

      // Hero Section
      case "hero":
        return (
          <>
            <Paper elevation={3} sx={{ mb: 4, overflow: "hidden" }}>
              <Typography
                variant="h6"
                gutterBottom
                align="center"
                sx={{ pt: 2 }}
              >
                Preview
              </Typography>
              <HeroSection config={heroConfig} />
            </Paper>
            <Paper elevation={3}>
              <Box p={3}>
                <HeroSectionGenerator
                  config={heroConfig}
                  onConfigChange={setHeroConfig}
                />
              </Box>
            </Paper>
          </>
        );

      // About PHCG
      case "PHCG":
        return (
          <>
            <Paper elevation={3} sx={{ mb: 4, overflow: "hidden" }}>
              <Typography
                variant="h6"
                gutterBottom
                align="center"
                sx={{ pt: 2 }}
              >
                Preview
              </Typography>
              <PreviewPanel config={PHCGConfig} />
            </Paper>
            <Paper elevation={3}>
              <Box p={3}>
                <PHCGGenerator
                  config={PHCGConfig}
                  onConfigChange={setPHCGConfig}
                />
              </Box>
            </Paper>
          </>
        );

      // What Is a PC?
      case "charter":
        return (
          <>
            <Paper elevation={3} sx={{ mb: 4, overflow: "hidden" }}>
              <Typography
                variant="h6"
                gutterBottom
                align="center"
                sx={{ pt: 2 }}
              >
                Preview
              </Typography>
              <CharterSectionPreview config={charterConfig} />
            </Paper>
            <Paper elevation={3}>
              <Box p={3}>
                <CharterSectionGenerator
                  config={charterConfig}
                  onConfigChange={setCharterConfig}
                />
              </Box>
            </Paper>
          </>
        );

      // Who Should Develop a Charter?
      case "charterTypes":
        return (
          <>
            <Paper elevation={3} sx={{ mb: 4, overflow: "hidden" }}>
              <Typography
                variant="h6"
                gutterBottom
                align="center"
                sx={{ pt: 2 }}
              >
                Preview
              </Typography>
              <CharterTypesPreview config={charterTypesConfig} />
            </Paper>
            <Paper elevation={3}>
              <Box p={3}>
                <CharterTypesGenerator
                  config={charterTypesConfig}
                  onConfigChange={setCharterTypesConfig}
                />
              </Box>
            </Paper>
          </>
        );

      // Three Steps to Partnership
      case "threeSteps":
        return (
          <>
            <Paper elevation={3} sx={{ mb: 4, overflow: "hidden" }}>
              <Typography
                variant="h6"
                gutterBottom
                align="center"
                sx={{ pt: 2 }}
              >
                Preview
              </Typography>
              <ThreeStepsPreview config={threeStepsConfig} />
            </Paper>
            <Paper elevation={3}>
              <Box p={3}>
                <ThreeStepsGenerator
                  config={threeStepsConfig}
                  onConfigChange={setThreeStepsConfig}
                />
              </Box>
            </Paper>
          </>
        );

      // The DYP Tool
      case "dypTool":
        return (
          <>
            <Paper elevation={3} sx={{ mb: 4, overflow: "hidden" }}>
              <Typography
                variant="h6"
                gutterBottom
                align="center"
                sx={{ pt: 2 }}
              >
                Preview
              </Typography>
              <DYPToolPreview config={dypToolConfig} />
            </Paper>
            <Paper elevation={3}>
              <Box p={3}>
                <DYPToolGenerator
                  config={dypToolConfig}
                  onConfigChange={setDypToolConfig}
                />
              </Box>
            </Paper>
          </>
        );

      // Partners Benefits
      case "partners":
        return (
          <>
            <Paper elevation={3} sx={{ mb: 4, overflow: "hidden" }}>
              <Typography
                variant="h6"
                gutterBottom
                align="center"
                sx={{ pt: 2 }}
              >
                Preview
              </Typography>
              <PartnersBenefitsPreview config={partnersBenefitsConfig} />
            </Paper>
            <Paper elevation={3}>
              <Box p={3}>
                <PartnersBenefitsGenerator
                  config={partnersBenefitsConfig}
                  onConfigChange={setPartnersBenefitsConfig}
                />
              </Box>
            </Paper>
          </>
        );

      // All the Critical Topics Are Covered
      case "pcProcess":
        return (
          <>
            <Paper elevation={3} sx={{ mb: 4, overflow: "hidden" }}>
              <Typography
                variant="h6"
                gutterBottom
                align="center"
                sx={{ pt: 2 }}
              >
                Preview
              </Typography>
              <PCProcessPreview config={pcProcessConfig} />
            </Paper>
            <Paper elevation={3}>
              <Box p={3}>
                <PCProcessGenerator
                  config={pcProcessConfig}
                  onConfigChange={setPcProcessConfig}
                />
              </Box>
            </Paper>
          </>
        );

      // DYP Advantages
      case "dypAdvantages":
        return (
          <>
            <Paper elevation={3} sx={{ mb: 4, overflow: "hidden" }}>
              <Typography
                variant="h6"
                gutterBottom
                align="center"
                sx={{ pt: 2 }}
              >
                Preview
              </Typography>
              <DYPAdvantagesPreview config={dypAdvantagesConfig} />
            </Paper>
            <Paper elevation={3}>
              <Box p={3}>
                <DYPAdvantagesGenerator
                  config={dypAdvantagesConfig}
                  onConfigChange={setDypAdvantagesConfig}
                />
              </Box>
            </Paper>
          </>
        );

      // Partners Get Assistance
      case "assistanceLevels":
        return (
          <>
            <Paper elevation={3} sx={{ mb: 4, overflow: "hidden" }}>
              <Typography
                variant="h6"
                gutterBottom
                align="center"
                sx={{ pt: 2 }}
              >
                Preview
              </Typography>
              <AssistanceLevelsPreview config={assistanceLevelsConfig} />
            </Paper>
            <Paper elevation={3}>
              <Box p={3}>
                <AssistanceLevelsGenerator
                  config={assistanceLevelsConfig}
                  onConfigChange={setAssistanceLevelsConfig}
                />
              </Box>
            </Paper>
          </>
        );

      // Partnership Charter Book
      case "bookPromo":
        return (
          <>
            <Paper elevation={3} sx={{ mb: 4, overflow: "hidden" }}>
              <Typography
                variant="h6"
                gutterBottom
                align="center"
                sx={{ pt: 2 }}
              >
                Preview
              </Typography>
              <BookPromoPreview config={bookPromoConfig} />
            </Paper>
            <Paper elevation={3}>
              <Box p={3}>
                <BookPromoGenerator
                  config={bookPromoConfig}
                  onConfigChange={setBookPromoConfig}
                />
              </Box>
            </Paper>
          </>
        );

      // Client Testimonials
      case "testimonials":
        return (
          <>
            <Paper elevation={3} sx={{ mb: 4, overflow: "hidden" }}>
              <Typography
                variant="h6"
                gutterBottom
                align="center"
                sx={{ pt: 2 }}
              >
                Preview
              </Typography>
              <TestimonialsPreview config={testimonialsConfig} />
            </Paper>
            <Paper elevation={3}>
              <Box p={3}>
                <TestimonialsGenerator
                  config={testimonialsConfig}
                  onConfigChange={setTestimonialsConfig}
                />
              </Box>
            </Paper>
          </>
        );

      // Slivers of Ambiguity
      case "sliversOfAmbiguity":
        return (
          <>
            <Paper elevation={3} sx={{ mb: 4, overflow: "hidden" }}>
              <Typography
                variant="h6"
                gutterBottom
                align="center"
                sx={{ pt: 2 }}
              >
                Preview
              </Typography>
              <SliversOfAmbiguityPreview config={sliversOfAmbiguityConfig} />
            </Paper>
            <Paper elevation={3}>
              <Box p={3}>
                <SliversOfAmbiguityGenerator
                  config={sliversOfAmbiguityConfig}
                  onConfigChange={setSliversOfAmbiguityConfig}
                />
              </Box>
            </Paper>
          </>
        );

      // DYP Licensed User
      case "dypLicensedUser":
        return (
          <>
            <Paper elevation={3} sx={{ mb: 4, overflow: "hidden" }}>
              <Typography
                variant="h6"
                gutterBottom
                align="center"
                sx={{ pt: 2 }}
              >
                Preview
              </Typography>
              <DYPLicensedUserPreview config={dypLicensedUserConfig} />
            </Paper>
            <Paper elevation={3}>
              <Box p={3}>
                <DYPLicensedUserGenerator
                  config={dypLicensedUserConfig}
                  onConfigChange={setDypLicensedUserConfig}
                />
              </Box>
            </Paper>
          </>
        );

      // Full Page Preview
      case "fullPagePreview":
        return (
          <FullPagePreview
            heroConfig={heroConfig}
            PHCGConfig={PHCGConfig}
            pcProcessConfig={pcProcessConfig}
            partnersBenefitsConfig={partnersBenefitsConfig}
            charterConfig={charterConfig}
            charterTypesConfig={charterTypesConfig}
            dypAdvantagesConfig={dypAdvantagesConfig}
            sliversOfAmbiguityConfig={sliversOfAmbiguityConfig}
            dypToolConfig={dypToolConfig}
            dypLicensedUserConfig={dypLicensedUserConfig}
            assistanceLevelsConfig={assistanceLevelsConfig}
            threeStepsConfig={threeStepsConfig}
            bookPromoConfig={bookPromoConfig}
            testimonialsConfig={testimonialsConfig}
          />
        );

      default:
        return <AppIntroduction />;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ display: "flex" }}>
        <Sidebar active={activeSection} onSelect={setActiveSection} />
        <Box component="main" sx={{ flexGrow: 1 }}>
          <Box
            sx={{
              px: { xs: 2, md: 4 },
              py: 2,
              borderBottom: "1px solid #e0e0e0",
              backgroundColor: STYLE_CONSTANTS.white,
              position: "sticky",
              top: 0,
              zIndex: 1000,
            }}
          >
            <Container maxWidth="lg">
              <Typography
                variant="h6"
                sx={{ fontWeight: "bold", color: "primary.main" }}
              >
                Your Firm's Partnership Charter Webpage Builder
              </Typography>
            </Container>
          </Box>

          <Box sx={{ p: 4 }}>
            <Container maxWidth="lg">
              {activeSection !== "introduction" && (
                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  You are editing:{" "}
                  <strong>{sectionTitles[activeSection]}</strong>
                </Typography>
              )}
              <Box
                key={activeSection}
                sx={{
                  p: { xs: 2, md: 4 },
                  backgroundColor: STYLE_CONSTANTS.lightGray,
                  borderRadius: STYLE_CONSTANTS.borderRadius,
                  opacity: 0,
                  transform: "translateY(20px)",
                  animation: "fadeIn 0.5s ease-in-out forwards",
                  "@keyframes fadeIn": {
                    from: {
                      opacity: 0,
                      transform: "translateY(20px)",
                    },
                    to: {
                      opacity: 1,
                      transform: "translateY(0)",
                    },
                  },
                }}
              >
                {renderSection()}
              </Box>
            </Container>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

// Main App component - routes between Wizard and Advanced mode
function App() {
  // Collect all block configurations for wizard
  const blockConfigs = {
    hero: {
      backgroundImageUrl: "/hero_bg.png",
      overlayColor: `${STYLE_CONSTANTS.primaryBlue}B3`,
      textColor: STYLE_CONSTANTS.white,
      headingFont: STYLE_CONSTANTS.headingFont,
      taglineText: "...never operate a partnership without one",
      logoUrl: "/phcg_logo.png",
      height: "50vh",
    },
    PHCG: {
      backgroundColor: STYLE_CONSTANTS.primaryBlue,
      textColor: STYLE_CONSTANTS.white,
      headingFont: STYLE_CONSTANTS.headingFont,
      bodyFont: STYLE_CONSTANTS.bodyFont,
      buttonColor: STYLE_CONSTANTS.accentGold,
      buttonText: null, // Will use dynamic text based on company name/acronym
      buttonRadius: STYLE_CONSTANTS.borderRadius,
    },
    charter: {
      backgroundColor: STYLE_CONSTANTS.primaryBlue,
      textColor: STYLE_CONSTANTS.white,
      headingColor: STYLE_CONSTANTS.white,
      accentColor: STYLE_CONSTANTS.accentGold,
      headingFont: STYLE_CONSTANTS.headingFont,
      bodyFont: STYLE_CONSTANTS.bodyFont,
      logoSrc: "/tpci_logo.svg",
      imageSrc: "/chartersection_computer.jpg",
    },
    charterTypes: {
      backgroundColor: STYLE_CONSTANTS.white,
      textColor: STYLE_CONSTANTS.darkText,
      headingColor: STYLE_CONSTANTS.primaryBlue,
      headingFont: STYLE_CONSTANTS.headingFont,
      bodyFont: STYLE_CONSTANTS.bodyFont,
    },
    threeSteps: {
      backgroundColor: STYLE_CONSTANTS.white,
      textColor: STYLE_CONSTANTS.darkText,
      headingColor: STYLE_CONSTANTS.primaryBlue,
      cardBackgroundColor: STYLE_CONSTANTS.cardGray,
      stepBadgeColor: STYLE_CONSTANTS.primaryBlue,
      stepBadgeTextColor: STYLE_CONSTANTS.white,
      headingFont: STYLE_CONSTANTS.headingFont,
      bodyFont: STYLE_CONSTANTS.bodyFont,
      discoverIconSrc: "https://tpci.vercel.app/step1.png",
      discussIconSrc: "https://tpci.vercel.app/step2.png",
      documentIconSrc: "https://tpci.vercel.app/step3.png",
      cardShadow: false,
    },
    dypTool: {
      backgroundColor: STYLE_CONSTANTS.lightGray,
      textColor: STYLE_CONSTANTS.darkText,
      headingColor: STYLE_CONSTANTS.primaryBlue,
      headingFont: STYLE_CONSTANTS.headingFont,
      bodyFont: STYLE_CONSTANTS.bodyFont,
      imageSrc: "/dyp_tool.png",
      imageRadius: STYLE_CONSTANTS.imageRadius,
    },
    partners: {
      backgroundColor: STYLE_CONSTANTS.lightGray,
      textColor: STYLE_CONSTANTS.darkText,
      headingColor: STYLE_CONSTANTS.primaryBlue,
      accentColor: STYLE_CONSTANTS.accentGold,
      headingFont: STYLE_CONSTANTS.headingFont,
      bodyFont: STYLE_CONSTANTS.bodyFont,
      buttonColor: STYLE_CONSTANTS.accentGold,
      buttonRadius: STYLE_CONSTANTS.borderRadius,
      imageRadius: STYLE_CONSTANTS.imageRadius,
      imageSrc: `https://placehold.co/600x400/e6e6e6/${STYLE_CONSTANTS.primaryBlue.replace(
        "#",
        ""
      )}?text=Partnership+diagram`,
    },
    pcProcess: {
      backgroundColor: STYLE_CONSTANTS.primaryBlue,
      textColor: STYLE_CONSTANTS.white,
      accentColor: STYLE_CONSTANTS.accentGold,
      headingFont: STYLE_CONSTANTS.headingFont,
      bodyFont: STYLE_CONSTANTS.bodyFont,
      buttonColor: STYLE_CONSTANTS.accentGold,
      buttonText: null, // Will use dynamic text based on company name/acronym
      buttonRadius: STYLE_CONSTANTS.borderRadius,
      accordionRadius: STYLE_CONSTANTS.borderRadius,
      transitionSpeed: STYLE_CONSTANTS.transitionSpeed,
    },
    dypAdvantages: {
      backgroundColor: STYLE_CONSTANTS.primaryBlue,
      textColor: STYLE_CONSTANTS.white,
      accentColor: STYLE_CONSTANTS.accentGold,
      headingFont: STYLE_CONSTANTS.headingFont,
      bodyFont: STYLE_CONSTANTS.bodyFont,
      accordionRadius: STYLE_CONSTANTS.borderRadius,
      transitionSpeed: STYLE_CONSTANTS.transitionSpeed,
    },
    assistanceLevels: {
      backgroundColor: STYLE_CONSTANTS.white,
      textColor: STYLE_CONSTANTS.darkText,
      headingColor: STYLE_CONSTANTS.primaryBlue,
      accentColor: STYLE_CONSTANTS.accentGold,
      headingFont: STYLE_CONSTANTS.headingFont,
      bodyFont: STYLE_CONSTANTS.bodyFont,
      imageRadius: STYLE_CONSTANTS.imageRadius,
      imageSrc: "/assistance_levels_diagram.png",
    },
    bookPromo: {
      backgroundColor: STYLE_CONSTANTS.accentGold,
      textColor: STYLE_CONSTANTS.white,
      headingFont: STYLE_CONSTANTS.headingFont,
      bodyFont: STYLE_CONSTANTS.bodyFont,
      borderRadius: 5,
      marginTop: STYLE_CONSTANTS.defaultMargin,
      marginBottom: STYLE_CONSTANTS.defaultMargin,
      bookImageSrc: "https://tpci.vercel.app/book_partnership_charter.png",
      contactLink: "https://example.com/contact",
      barnesNobleLogo: "https://tpci.vercel.app/logo_barnes_noble.png",
      googlePlayLogo: "https://tpci.vercel.app/logo_google_play.png",
      amazonLogo: "https://tpci.vercel.app/logo_amazon.png",
      indieBoundLogo: "https://tpci.vercel.app/logo_indie_bound.png",
      audibleLogo: "https://tpci.vercel.app/logo_audible.png",
      invertLogos: false,
    },
    testimonials: {
      backgroundColor: STYLE_CONSTANTS.primaryBlue,
      textColor: STYLE_CONSTANTS.white,
      headingFont: STYLE_CONSTANTS.headingFont,
      bodyFont: STYLE_CONSTANTS.bodyFont,
      testimonialImage: "https://tpci.vercel.app/testimonial_photo.jpeg",
    },
    sliversOfAmbiguity: {
      backgroundColor: STYLE_CONSTANTS.lightGray,
      textColor: STYLE_CONSTANTS.darkText,
      headingColor: STYLE_CONSTANTS.primaryBlue,
      accentColor: STYLE_CONSTANTS.accentGold,
      headingFont: STYLE_CONSTANTS.headingFont,
      bodyFont: STYLE_CONSTANTS.bodyFont,
    },
    dypLicensedUser: {
      backgroundColor: STYLE_CONSTANTS.primaryBlue,
      textColor: STYLE_CONSTANTS.white,
      accentColor: STYLE_CONSTANTS.accentGold,
      headingFont: STYLE_CONSTANTS.headingFont,
      bodyFont: STYLE_CONSTANTS.bodyFont,
      buttonText: null,  // Will use dynamic text based on company acronym
      buttonRadius: STYLE_CONSTANTS.borderRadius,
      organizationName: null,  // Will use company acronym or placeholder
    },
  };

  return (
    <WizardApp
      theme={theme}
      existingApp={<LegacyApp />}
      blockConfigs={blockConfigs}
    />
  );
}

export default App;
